import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import Moment from 'moment';
import isEqual from 'lodash.isequal';
import * as types from '../../../constants/actionTypes';
import { actions as locationTaskActions } from '../../../actions/locationTaskActions';
import { actions as subscriptionActions } from '../../../actions/subscriptionActions';
import { actions as locationDashboardActions } from '../../../actions/locationDashboardActions';
import { actions as filesActions } from '../../../actions/filesActions';
import { actions as locationActions } from '../../../actions/locationActions';
import {
    DEFAULT_PAGE_SIZE,
    MOBILE_WINDOW_BREAKPOINT,
    TASK_LIST_VIEWS as VIEWS,
    TASK_LIST_URL_PARAMS as URLParams,
    CORE_STATUSES,
    GANTT_ZOOM_LEVELS,
} from '../../../constants/constants';
import Notification from '../../Common/Notification';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import './TaskListPage.scss';
import TaskListHeader from './TaskListHeader/TaskListHeader.js';
import ListView from './ListView/ListView.js';
import ListViewMobile from './ListViewMobile/ListViewMobile.js';
import KanbanView from '../KanbanView';
import CalendarView from '../Calendar/CalendarView';
import GanttChartView2 from '../GanttChart/GanttChartView2';
import ResourceAllocationView from '../ResourceAllocation/ResourceAllocationView';
import TaskDetailManager from '../TaskDetail/TaskDetailManager';
import ColumnFiltersDrawer from '../ColumnFiltersDrawer';
import BulkUpdateResponsibleUserDrawer from '../BulkUpdateResponsibleUserDrawer';
import BulkUpdateStatusDrawer from '../BulkUpdateStatusDrawer';
import AssignTasksDrawer from '../../LocationDashboard/Widgets/AssignTasksDrawer';
import _ from 'lodash';
import DocumentTitle from 'react-document-title';
import ShowFiltersDrawer from '../ShowFIltersDrawer';
import ReportFooter from '../../Common/ReportFooter/ReportFooter';
import { ConvertDayOfWeekToCalendarStartDay } from '../../../helpers/calendar';
import { DATE_FORMAT, TASK_LIST_MENU_ITEMS } from '../../../constants/constants';
import Modal61 from '../../Common/Modal/Modal61';
import InputText from '../../Common/InputText';

export class TaskListPage extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            isLoading: false,
            isLoadingListFilters: false,
            isLoadingColumnFilters: false,
            isLoadingSearchParams: true,

            lastSearchTime: new Date(),

            isInitialLoadComplete: false,
            haveSearchParamsChanged: false,
            locationTasks: [],
            totalCount: 0,
            isMissingActiveLocations: false,
            selectedView: VIEWS.List.value,
            isColumnFiltersDrawerOpen: false,
            allTasksChecked: false,
            bulkUpdateMode: false,
            locationUsers: [],

            // Task Detail State
            isTaskDetailOpen: false,
            editTaskId: 0,
            editTask: {},
            editLocationKey: '',

            isBulkUpdateResponsibleUserDrawerOpen: false,
            isBulkUpdateStatusDrawerOpen: false,
            isShowFiltersDrawerOpen: false,
            isBulkUpdateByJobTitleOpen: false,

            filters: {
                isStartDateActive: false,
                isTaskDependenciesActive: false,
                isTaskStepsActive: false,
                isProjectWidgetActive: false,
                allowLocationTaskDateChanges: false,
                savedColumns: [],
                masterColumns: [],
                calendarStartDay: 0, // sunday
                ganttZoomLevel: GANTT_ZOOM_LEVELS.Month.value,
                ganttExportToPDF: false,
                ganttExportToPNG: false,
            },
            columnFilters: {
                projects: [],
                communities: [],
                disciplines: [],
                departments: [],
                phases: [],
                statuses: [],
                startDates: [],
                targetDates: [],
                tags: [],
            },
            defaultSearchParams: {
                isMobile: false,
                pageSize: DEFAULT_PAGE_SIZE,
                pageNumber: 1,
                sortBy: 'targetDate',
                sortDir: 'asc',
                name: '',
                locationKeys: this.props.taskListState.searchParams.locationKeys,
                taskId: '',
                projectIds: [],
                targetDateStart: null,
                targetDateEnd: null,
                startDateStart: null,
                startDateEnd: null,
                responsibleUserName: '',
                statusIds: [],
                departmentIds: [],
                disciplineIds: [],
                communityIds: [],
                phaseIds: [],
                tagIds: [],
                additionalInfo: '',
                baselineName: '',
                recentlyUpdated: null,
                isCalendarView: false,
            },
            isSaveFiltersModalFormValid: true,
            customFiltersName: '',
            isSaveCustomViewModalLoading: false,
            isSaveAndManageTaskFiltersActive: false,
        };

        this.saveFiltersModal = createRef();
    }

    isMobile = () => {
        return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
    };

    componentDidMount = async () => {
        try {
            await this.setUrlParams();
            await this.loadListFilters();
            await this.loadColumnFilters();

            await this.search();
            this.setState({ isInitialLoadComplete: true, haveSearchParamsChanged: false });
            await this.getResponsibleUsersForLocations();
            /**
             * If filtering down to a single task, open that task immediately on
             * page load
             */
            if (this.props.urlParams && this.props.urlParams.taskId) {
                this.showTask(Number.parseInt(this.props.urlParams.taskId));
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };


    hasUrlParams = () => {
        const urlParams = this.props.urlParams;
        return (
            urlParams &&
            ((urlParams.locationKeys && urlParams.locationKeys.length > 0) ||
                (urlParams.taskId && urlParams.taskId.trim().length > 0) ||
                (urlParams.projectIds && urlParams.projectIds.length > 0) ||
                (urlParams.targetDateStart && urlParams.targetDateStart.trim().length > 0) ||
                (urlParams.targetDateEnd && urlParams.targetDateEnd.trim().length > 0) ||
                (urlParams.startDateStart && urlParams.startDateStart.trim().length > 0) ||
                (urlParams.startDateEnd && urlParams.startDateEnd.trim().length > 0) ||
                (urlParams.responsibleUserName && urlParams.responsibleUserName.trim().length > 0) ||
                (urlParams.statusIds && urlParams.statusIds.length > 0) ||
                (urlParams.departmentIds && urlParams.departmentIds.length > 0) ||
                (urlParams.disciplineIds && urlParams.disciplineIds.length > 0) ||
                (urlParams.tagIds && urlParams.tagIds.length > 0) ||
                /**
                 * recentlyUpdated is eventually a boolean value that's null by default
                 * so we can check whether or not it actually had a value on the url
                 * params.  Both false or true values indicate that we DO have a value
                 * for it from the URL params, so we should return that we have URL params
                 * so we can use them to override the existing state of the task list
                 * search params.
                 */
                urlParams.recentlyUpdated === true ||
                urlParams.recentlyUpdated === false ||
                urlParams.allTasks === true ||
                urlParams.allTasks === false ||
                urlParams.allLocations === true ||
                urlParams.allLocations === false)
        );
    };

    setUrlParams = async () => {
        try {
            let searchParams = {
                ...this.props.taskListState.searchParams,
                isMobile: this.isMobile(),
                pageSize: DEFAULT_PAGE_SIZE,
                pageNumber: 1,
                sortBy: 'targetDate',
                sortDir: 'asc',
                name: '',
                additionalInfo: '',
                baselineName: '',
            };
            if (this.hasUrlParams()) {
                /**
                 * If there are URL Params, but locationKeys are not set in those URL params,
                 * then we do not want to clear them out of the search params.  We instead want
                 * to default them to the saved redux searchParams
                 */
                let urlParams = {
                    ...this.props.urlParams,
                };
                if (!urlParams.locationKeys || !urlParams.locationKeys.length) {
                    urlParams.locationKeys = searchParams.locationKeys;
                }

                if (urlParams.allLocations) {
                    // load all locations to get their keys and add them to the search paramss
                    const response = await this.props.locationActions.searchByName(null);
                    if (response.payload.locations.length > 0) {
                        urlParams.locationKeys = response.payload.locations.map((m) => m.id);
                        const searchParams = {
                            ...this.props.taskListState.searchParams,
                            locationKeys: urlParams.locationKeys,
                        };
                        await this.props.locationTaskActions.setLocationFilterState(searchParams);
                    }
                }

                if (urlParams.allLocations === 'false') {
                    const searchParams = {
                        ...this.props.taskListState.searchParams,
                        locationKeys: [],
                    };
                    await this.props.locationTaskActions.setLocationFilterState(searchParams);
                }

                if (urlParams.dropDownOption) {
                    await this.props.locationTaskActions.setTasksMenuSelected(
                        Object.values(TASK_LIST_MENU_ITEMS).find((i) => i.value === urlParams.dropDownOption).ordinal - 1
                    );
                }

                if (urlParams.allTasks) {
                    /**
                     * When the all tasks URL parameter is passed in, set search params equal to
                     * the default search params, thus clearing the filters
                     */
                    searchParams = { ...this.state.defaultSearchParams };
                } else {
                    searchParams = {
                        ...searchParams,
                        ...urlParams,
                    };
                }
            }
            await this.setSearchParams(searchParams);
            // set this to true so the header will render because it needs the params
            this.setState({ isLoadingSearchParams: false });
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    setSearchParams = async (searchParams) => {
        try {
            /**
             * We need to keep track of when search params have actually been
             * changed, so that we can intelligently trigger the clearing of any
             * URL params when this.search() completes successfully.  There are
             * times (such as editing a task's status or opening its task detail
             * drawer and saving some changes) where we want to trigger a new
             * search, but we want to keep the existing URL params/search params
             * untouched.  In those cases, haveSearchParamsChanged will still
             * be false after the initial page load sets it, and we'll know not
             * to clear out the now inaccurate URL params.
             */
            this.setState({ haveSearchParamsChanged: true }, async () => {
                await this.props.locationTaskActions.setLocationFilterState(searchParams);
                await this.loadColumnFilters();
            });
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    updateSearchParams = async (key, value, search) => {
        // Create new parameters only if the key and value are valid
        const newParams = key
            ? {
                ...this.props.taskListState.searchParams,
                [key]: value,
            }
            : this.props.taskListState.searchParams;

        // Check if search parameters have changed
        const haveSearchParamsChanged = key && !isEqual(newParams[key], this.props.taskListState.searchParams[key]);

        if (haveSearchParamsChanged) {
            // Update state and Redux filter state
            this.setState({ haveSearchParamsChanged: true });
            this.props.locationTaskActions.setLocationFilterState(newParams);
        }

        // Perform a search if requested
        if (search === true) {
            // Build query string based on updated parameters
            const queryString = Object.entries(newParams)
                .filter(([key, value]) => {
                    const alias = URLParams[key.charAt(0).toUpperCase() + key.slice(1)]; // Map key to URL param alias

                    // Special case: exclude 'l' for locations
                    if (alias === 'l') {
                        return false;
                    }

                    return alias && value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : value !== '');
                })
                .flatMap(([key, value]) => {
                    const alias = URLParams[key.charAt(0).toUpperCase() + key.slice(1)];
                    return Array.isArray(value)
                        ? value.map((v) => `${alias}=${encodeURIComponent(v)}`) // For arrays, multiple key-value pairs
                        : `${alias}=${encodeURIComponent(value)}`; // For non-arrays, single key-value pair
                })
                .join('&');

            // Update the browser URL with the new query string
            this.props.history.push({
                pathname: this.props.history.location.pathname,
                search: queryString ? `?${queryString}` : '',
            });

            await this.search(true, newParams); // Trigger the search method
        }
    };

    loadListFilters = async () => {
        this.setState({ isLoadingListFilters: true });
        try {
            if (this.props.subscriptionId) {
                const response = await this.props.locationTaskActions.loadListFilters();
                if (response.type === types.LOAD_LOCATION_TASK_LIST_FILTERS_SUCCESS) {
                    const newFilters = {
                        ...this.state.filters,
                        isStartDateActive: response.payload.isStartDateActive,
                        isTaskDependenciesActive: response.payload.isTaskDependenciesActive,
                        isTaskStepsActive: response.payload.isTaskStepsActive,
                        isProjectWidgetActive: response.payload.isProjectWidgetActive,
                        allowLocationTaskDateChanges: response.payload.allowLocationTaskDateChanges,
                        savedColumns: response.payload.savedColumns,
                        masterColumns: response.payload.masterColumns,
                        calendarStartDay: ConvertDayOfWeekToCalendarStartDay(response.payload.calendarStartDay),
                    };

                    this.setAlwaysDisplayedColumns(newFilters.masterColumns, newFilters.savedColumns, 'locationName');
                    this.setState({ filters: newFilters });
                    this.setState({ isSaveAndManageTaskFiltersActive: response.payload.isSaveAndManageTaskFiltersActive });

                    // Store/update custom user defined menu items in redux if they've changed
                    if (!_.isEqual(this.props.tasksPageTasksMenuItems, response.payload.filters || [])) {
                        await this.props.locationTaskActions.setTasksPageTaskMenuItems(response.payload.filters || []);
                    }
                } else if (response.type === types.LOAD_LOCATION_TASK_LIST_FILTERS_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            this.setState({ isLoadingListFilters: false });
        }
    };

    setAlwaysDisplayedColumns = (masterColumns, currentSavedColumns, fieldName) => {
        //set these columns to always display regardless of user saved settings
        if (currentSavedColumns.length > 0 && !currentSavedColumns.some((col) => col.fieldName === fieldName)) {
            let targetColumn = masterColumns.find((col) => col.fieldName === fieldName);
            currentSavedColumns.push(targetColumn);
        }
    };

    loadColumnFilters = async (search) => {
        // needs to fetch every time locationKeys change
        this.setState({ isLoadingColumnFilters: true });
        try {
            if (this.props.subscriptionId) {
                const response = await this.props.locationTaskActions.loadColumnFilters(
                    this.props.subscriptionId,
                    this.props.taskListState.searchParams.locationKeys
                );
                if (response.type === types.LOAD_LOCATION_TASK_LIST_COLUMN_FILTERS_SUCCESS) {
                    const newFilters = {
                        ...this.state.columnFilters,
                        startDates: response.payload.startDates,
                        targetDate: response.payload.targetDates,
                        projects: response.payload.projects,
                        communities: response.payload.communities,
                        disciplines: response.payload.disciplines,
                        departments: response.payload.departments,
                        phases: response.payload.phases,
                        statuses: response.payload.statuses,
                        tags: response.payload.tags,
                    };

                    // make sure all statuses in the search params are in these filters, otherwise remove them from the search params
                    let newStatusIds = [];
                    for (let i = 0; i < this.props.taskListState.searchParams.statusIds.length; i++) {
                        let statusId = this.props.taskListState.searchParams.statusIds[i];
                        if (newFilters.statuses.find((m) => m.id == statusId)) {
                            newStatusIds.push(statusId);
                        }
                    }

                    this.setState({ columnFilters: newFilters }, () => {
                        this.updateSearchParams('statusIds', newStatusIds, search);
                    });
                } else if (response.type === types.LOAD_LOCATION_TASK_LIST_COLUMN_FILTERS_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            this.setState({ isLoadingColumnFilters: false });
        }
    };

    updateTaskDates = async (taskId, startDate, targetDate) => {
        try {
            // updates the start and end dates of a task in the db
            const response = await this.props.locationTaskActions.updateDates(taskId, startDate, targetDate);
            if (response.type === types.UPDATE_LOCATION_TASK_DATES_SUCCESS) {
                Notification('success', '', 'Task updated');
                this.search(false);
            } else if (response.type === types.UPDATE_LOCATION_TASK_DATES_FAILURE) {
                Notification('error', 'Error', response.payload.message);
                this.setState({ lastSearchTime: new Date() });
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    updateContingent = async (taskId, contingentId) => {
        try {
            // contingentId will be null on removal of gantt chart link
            const response = await this.props.locationTaskActions.updateContingent(taskId, contingentId);
            if (response.type === types.UPDATE_LOCATION_CONTINGENT_SUCCESS) {
                Notification('success', '', 'Task updated');
                this.search(false);
            } else if (response.type === types.UPDATE_LOCATION_CONTINGENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    setLocationKeys = async (locationKeys) => {
        try {
            const searchParams = {
                ...this.props.taskListState.searchParams,
                locationKeys: locationKeys,
            };
            await this.setSearchParams(searchParams);

            const newDefaultSearchParams = {
                ...this.state.defaultSearchParams,
                locationKeys: locationKeys,
            };
            this.setState({ defaultSearchParams: newDefaultSearchParams });
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    search = async (showLoadingIndicator, overrideParams = null) => {
        try {
            /*
             * When a task is updated we do not want to use the loading indicator
             * We just want the results to update on the screen so the user doesn't lose their spot
             */
            const isLoading = showLoadingIndicator != undefined ? showLoadingIndicator : true;
            this.setState({ isLoading: isLoading });

            let searchParams = overrideParams || this.props.taskListState.searchParams;

            let request = {
                ...searchParams,
                isCalendarView: false,
            };

            if (this.state.selectedView === VIEWS.Calendar.value) {
                const { targetDateStart, targetDateEnd } = searchParams;
                request = {
                    ...searchParams,
                    isCalendarView: true,
                    targetDateStart: targetDateStart ? Moment.utc(targetDateStart).format(DATE_FORMAT) : null,
                    targetDateEnd: targetDateEnd ? Moment.utc(targetDateEnd).format(DATE_FORMAT) : null,
                };
            }

            const response = await this.props.locationTaskActions.search(request);
            if (response.type === types.SEARCH_LOCATION_TASK_LIST_SUCCESS) {
                if (response.payload.isMissingActiveLocations) {
                    this.setState({ isMissingActiveLocations: response.payload.isMissingActiveLocations });
                } else {
                    const tasks = response.payload.results.map((item) => {
                        return { ...item, isChecked: false };
                    });
                    const dateValue = new Date();
                    this.setState({ locationTasks: tasks, totalCount: response.payload.totalCount, lastSearchTime: dateValue });
                    /**
                     * When there are results, we should ensure that the default location key is
                     * selected in the location selector.  Even when a user has cleared out the list
                     * or just come to the page for the first time, the search results will contain
                     * results for the user's default location.  That means if a user has access to
                     * their default location, then it's impossible for them to select no locations
                     * or see no results, unless their default location has no tasks.
                     */
                    const redux = this.props.taskListState;
                    const noSelectedLocations =
                        !redux.searchParams || !redux.searchParams.locationKeys || redux.searchParams.locationKeys.length <= 0;
                    const hasDefaultLocation = redux.defaultLocationKey && redux.defaultLocationKey.length > 0;
                    if (noSelectedLocations && hasDefaultLocation) {
                        await this.setLocationKeys([redux.defaultLocationKey]);
                    }
                }
            } else if (response.type === types.SEARCH_LOCATION_TASK_LIST_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    onCalendarDateChange = async (start, end) => {
        const searchParams = {
            ...this.props.taskListState.searchParams,
            targetDateStart: start,
            targetDateEnd: end,
        };

        await this.setSearchParams(searchParams);
        await this.search(false);
    };

    onLocationChange = async (locationKeys, skipSearch = false) => {
        await this.setLocationKeys(locationKeys);
        if (!skipSearch) {
            const searchParams = {
                ...this.props.taskListState.searchParams,
                pageNumber: 1,
            };
            this.setSearchParams(searchParams);
            await this.search();
        }

        await this.getResponsibleUsersForLocations();
    };

    getResponsibleUsersForLocations = async () => {
        try {
            const locKeys = this.props.taskListState.searchParams.locationKeys;

            if (!Array.isArray(locKeys) || locKeys.length === 0) {
                console.warn('No location keys provided');
                return;
            }

            const response = await this.props.locationTaskActions.searchResponsibleUsers(locKeys);
            if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS) {
                this.setState({ locationUsers: response.payload.users });
            } else if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    getSelectedLocations = () => {
        let locationObjs = [];
        this.props.taskListState.searchParams.locationKeys.forEach((locationKey) => {
            const location = this.props.taskListState.locations.find((m) => m.id === locationKey);
            locationObjs.push(location);
        });
        return locationObjs;
    };

    onSelectTask = (task) => {
        this.showTask(task.id);
    };

    onViewChange = (viewName) => {
        const reduxParams = this.props.taskListState.searchParams;
        let searchParams = {};

        if (viewName === VIEWS.List.value) {
            // keep all the filters the same, but reset the paging and the dates because the calendar modifies the dates
            searchParams = {
                ...reduxParams,
                pageNumber: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                targetDateStart: null,
                targetDateEnd: null,
                isMilestone: null,
                sortBy: 'TargetDate, Name',
            };
        } else if (viewName === VIEWS.Calendar.value) {
            // keep all the filters the same, but bump up the pagesize so we get all the tasks
            searchParams = {
                ...reduxParams,
                pageNumber: 1,
                pageSize: 1000,
                isMilestone: null,
            };
            ReactGA.event({
                category: 'Task List Page',
                action: 'Toggle Calendar',
            });
        } else if (viewName === VIEWS.Gantt.value) {
            // only show milestones by default
            searchParams = {
                ...reduxParams,
                pageNumber: 1,
                pageSize: 10000,
                targetDateStart: null,
                targetDateEnd: null,
                isMilestone: null,
                sortBy: 'LocationId, ProjectName, TargetDate', // important for looping in the GanttChartView
            };
            ReactGA.event({
                category: 'Task List Page',
                action: 'Toggle Gantt Chart',
            });
        } else if (viewName === VIEWS.ResourceAllocation.value) {
            // all other views use the same search, this one has its own api handled internally within the view component
            // we still want to clear out the target dates though
            searchParams = {
                ...reduxParams,
                targetDateStart: null,
                targetDateEnd: null,
            };
            ReactGA.event({
                category: 'Task List Page',
                action: 'Toggle Workload View',
            });
        }

        this.setState({ selectedView: viewName, locationTasks: [] }, async () => {
            let newParams = {
                ...this.props.taskListState.searchParams,
                ...searchParams,
            };

            // Bypass the other functions that update searchParams and just do what we need here
            // The other ones mark haveSearchParamsChanged to true, which will refresh the page
            // and if there are any urlParams, we would lose them. We don't want that when sorting
            await this.props.locationTaskActions.setLocationFilterState(newParams);
            await this.search(true);
        });
    };

    onColumnsClick = () => {
        this.setState({ isColumnFiltersDrawerOpen: true });
    };

    onColumnFiltersComplete = async (arrayOfColumnIds) => {
        this.setState({ isLoading: true, isColumnFiltersDrawerOpen: false });
        try {
            if (this.props.subscriptionId) {
                const response = await this.props.locationTaskActions.updateSavedColumns(arrayOfColumnIds);
                if (response.type === types.UPDATE_LOCATION_TASK_LIST_COLUMNS_SUCCESS) {
                    // update this.state.filters.savedColumns with the results
                    const newFilters = {
                        ...this.state.filters,
                        savedColumns: response.payload.savedColumns,
                    };

                    this.setAlwaysDisplayedColumns(newFilters.masterColumns, newFilters.savedColumns, 'locationName');
                    this.setState({ filters: newFilters });
                } else if (response.type === types.UPDATE_LOCATION_TASK_LIST_COLUMNS_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    onColumnFiltersCancel = () => {
        this.setState({ isColumnFiltersDrawerOpen: false });
    };

    onResetColumnFilters = () => {
        let arrayOfColumnIds = [];
        for (let i = 0; i < this.state.filters.masterColumns.length; i++) {
            arrayOfColumnIds.push(this.state.filters.masterColumns[i].id);
        }
        this.onColumnFiltersComplete(arrayOfColumnIds);
    };

    onColumnSort = async (sortBy, sortDir) => {
        try {
            const reduxParams = this.props.taskListState.searchParams;
            // Only search again if the values have changed
            if (reduxParams.sortBy !== sortBy || reduxParams.sortDir !== sortDir) {
                const searchParams = {
                    ...reduxParams,
                    sortBy: sortBy,
                    sortDir: sortDir,
                };

                let newParams = {
                    ...this.props.taskListState.searchParams,
                    ...searchParams,
                };

                // Bypass the other functions that update searchParams and just do what we need here
                // The other ones mark haveSearchParamsChanged to true, which will refresh the page
                // and if there are any urlParams, we would lose them. We don't want that when sorting
                await this.props.locationTaskActions.setLocationFilterState(newParams);
                await this.search();
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    bulkUpdateComplete = () => {
        this.bulkUpdateCancel();
        this.search();
    };

    completeBulkUpdateStatus = () => {
        this.bulkUpdateCancel();
        this.search();
    };

    bulkUpdateCancel = () => {
        this.setState({ isBulkUpdateResponsibleUserDrawerOpen: false, isBulkUpdateStatusDrawerOpen: false });
    };

    showTask = (id) => {
        /**
         * If a task is found in the list, grab its location key from it, otherwise
         * assume we're adding a location task, and get the single location key
         * from the single selected location in the location selector.  Set
         * this locationKey along with the taskId in state, so we can pass it to
         * the proper drawer
         */
        let locationKey = null;
        const task = this.state.locationTasks.find((x) => x.id == id);
        if (task) {
            locationKey = task.locationKey;
        } else {
            const primaryLocation = this.getPrimaryLocation();
            if (primaryLocation) {
                locationKey = primaryLocation.locationKey;
            }
        }
        this.setState({
            isTaskDetailOpen: true,
            editTaskId: id,
            editLocationKey: locationKey,
        });
    };

    bulkUpdateInactive = async () => {
        const checkedTasks = this.state.locationTasks.find((m) => m.isChecked === true);
        if (!checkedTasks || checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
        } else {
            try {
                this.setState({ isLoading: true });
                const result = await this.props.locationTaskActions.updateIsActive(
                    this.state.locationTasks.filter((m) => m.isChecked).map((m) => m.id)
                );
                if (result.type === types.UPDATE_LOCATION_TASK_LIST_ISACTIVE_SUCCESS) {
                    if (result?.payload?.invalidLocationTasks?.length > 0) {
                        const invalidTasks = result.payload.invalidLocationTasks.map((bt) => bt.name);
                        const msg =
                            'The relationship to the prerequisite needs to be removed before this task(s) can be made inactive: <br/>' +
                            invalidTasks.join('</br>');
                        Notification('error', 'Location Task Contingent Violation', msg);
                    } else {
                        const displayActive = this.state.isActive ? 'Active' : 'Inactive';
                        Notification('success', 'Success', 'Tasks(s) successfully marked as ' + displayActive);
                        this.bulkUpdateComplete();
                    }
                } else if (result.type === types.UPDATE_LOCATION_TASK_LIST_ISACTIVE_FAILURE) {
                    Notification('error', 'Error', result.payload.message);
                }
            } catch (error) {
                Notification('error', 'Error', error);
            } finally {
                this.setState({ isLoading: false });
            }
        }
    };

    bulkUpdateStatus = () => {
        const checkedTasks = this.state.locationTasks.find((m) => m.isChecked === true);
        if (!checkedTasks || checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
        } else {
            this.setState({ isBulkUpdateStatusDrawerOpen: true });
        }
    };

    bulkUpdateResponsibleUser = () => {
        const checkedTasks = this.state.locationTasks.find((m) => m.isChecked === true);
        if (!checkedTasks || checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
        } else {
            this.setState({ isBulkUpdateResponsibleUserDrawerOpen: true });
        }
    };

    bulkUpdateByJobTitle = () => {
        this.setState({ isBulkUpdateByJobTitleOpen: true });
    };

    bulkUpdateByJobTitleComplete = () => {
        this.setState({ isBulkUpdateByJobTitleOpen: false }, () => this.search(false));
    };

    bulkUpdateByJobTitleCancel = () => {
        this.setState({ isBulkUpdateByJobTitleOpen: false });
    };

    onAssignToMeComplete = () => {
        this.search(false);
    };

    onStatusChangedComplete = () => {
        this.search(false);
    };

    onTaskDetailComplete = () => {
        this.setState({ isTaskDetailOpen: false }, () => this.search(false));
    };

    onTaskDetailCancel = () => {
        this.setState({ isTaskDetailOpen: false });
    };

    changeBulkUpdateMode = () => {
        const bulkModeState = !this.state.bulkUpdateMode ? 'On' : 'Off';
        const actionName = 'Toggle Bulk Update ' + bulkModeState;

        ReactGA.event({
            category: 'Task List Page',
            action: actionName,
        });

        this.setState({ bulkUpdateMode: !this.state.bulkUpdateMode });
    };

    onToggleCheckedTask = (locationTaskId) => {
        const tasks = this.state.locationTasks;
        let checkedTasks = [];

        const newTasks = tasks.map((item) => {
            let task = { ...item };
            if (item.id === locationTaskId) {
                task.isChecked = !item.isChecked;
            }

            if (task.isChecked) {
                checkedTasks.push(task);
            }
            return task;
        });

        // If we've manually checked all tasks, also check the master checkbox
        const allTasksChecked = newTasks.length === checkedTasks.length;

        this.setState({
            checkedTasks: checkedTasks,
            locationTasks: newTasks,
            allTasksChecked: allTasksChecked,
        });

        return checkedTasks.length;
    };

    onMasterCheckboxToggle = () => {
        // Toggle the master checkbox to checked or unchecked
        const allTasksChecked = !this.state.allTasksChecked;

        // Check or uncheck all location tasks, based on its newly-toggled state
        const newTasks = this.state.locationTasks.map((item) => {
            return { ...item, isChecked: allTasksChecked };
        });
        const checkedTasks = allTasksChecked ? newTasks.slice() : [];

        this.setState({
            checkedTasks: checkedTasks,
            locationTasks: newTasks,
            allTasksChecked: allTasksChecked,
        });

        return checkedTasks.length;
    };

    clearAllCheckedTasks = () => {
        const locationTasks = this.state.locationTasks.map((item) => {
            return { ...item, isChecked: false };
        });
        this.setState({
            checkedTasks: [],
            allTasksChecked: false,
            locationTasks: locationTasks,
        });
    };

    clearAllFilters = async () => {
        await this.setSearchParams(this.state.defaultSearchParams);
        await this.search();
    };

    getPrimaryLocation = () => {
        // If only one location is selected, then the Add Task button is enabled, and this location
        // is where the task is added.
        const locationKeys = this.props.taskListState.searchParams.locationKeys;
        const locations = this.props.taskListState.locations;
        let primaryLocationKey = null;
        let primaryLocation = null;
        if (locationKeys && locationKeys.length === 1 && locations && locations.length > 0) {
            primaryLocationKey = locationKeys[0];
            primaryLocation = locations.find((x) => x.id === primaryLocationKey);
            return {
                ...primaryLocation,
                locationKey: primaryLocationKey,
            };
        }
        return null;
    };

    onShowFiltersDrawer = () => {
        this.setState({ isShowFiltersDrawerOpen: true });
    };

    onFiltersDrawerComplete = async (newParams) => {
        let mergedParams = {
            ...this.props.taskListState.searchParams,
            ...newParams,
        };

        await this.setState({ haveSearchParamsChanged: true, isShowFiltersDrawerOpen: false }, async () => {
            await this.setSearchParams(mergedParams);
            await this.search(true);
        });
    };

    onFiltersDrawerCancel = () => {
        this.setState({ isShowFiltersDrawerOpen: false });
    };

    renderView = () => {
        const isLoadingAnything = this.state.isLoading || this.state.isLoadingColumnFilters || this.state.isLoadingListFilters ? true : false;

        switch (this.state.selectedView) {
            case VIEWS.Kanban.value: {
                return <KanbanView locationTasks={this.state.locationTasks} totalCount={this.state.totalCount} />;
            }
            case VIEWS.Calendar.value: {
                return (
                    <CalendarView
                        locationTasks={this.state.locationTasks}
                        onSelectTask={this.onSelectTask}
                        isFetchingTasks={this.state.isLoading}
                        columnFilters={this.state.columnFilters}
                        settings={this.state.filters}
                        updateTaskDates={this.updateTaskDates}
                        lastSearchTime={this.state.lastSearchTime}
                        onCalendarDateChange={this.onCalendarDateChange}
                        onStatusChangedComplete={this.onStatusChangedComplete}
                    />
                );
            }
            case VIEWS.Gantt.value: {
                return (
                    <GanttChartView2
                        locationTasks={this.state.locationTasks}
                        onSelectTask={this.onSelectTask}
                        isFetchingTasks={this.state.isLoading}
                        columnFilters={this.state.columnFilters}
                        settings={this.state.filters}
                        locations={this.getSelectedLocations()}
                        updateTaskDates={this.updateTaskDates}
                        updateContingent={this.updateContingent}
                        lastSearchTime={this.state.lastSearchTime}
                    />
                );
            }
            case VIEWS.ResourceAllocation.value:
                return (
                    <ResourceAllocationView
                        settings={this.state.filters}
                        locationTaskActions={this.props.locationTaskActions}
                        locations={this.getSelectedLocations()}
                        searchParams={this.props.taskListState.searchParams}
                        lastSearchTime={this.state.lastSearchTime}
                        showTask={this.showTask}
                    />
                );
            case VIEWS.List.value:
            default: {
                if (this.isMobile()) {
                    return (
                        <ListViewMobile
                            locationTasks={this.state.locationTasks}
                            totalCount={this.state.totalCount}
                            filters={this.state.filters}
                            onSelectTask={this.onSelectTask}
                            isFetchingTasks={this.state.isLoading}
                        />
                    );
                } else {
                    const { isLoading, isLoadingListFilters, isLoadingColumnFilters } = this.state;

                    const isStillLoading = isLoading || isLoadingListFilters || isLoadingColumnFilters;

                    if (isStillLoading || !this.state.columnFilters.statuses.length) {
                        return <div className='p-4'>Loading...</div>;
                    }

                    return (
                        <ListView
                            locationTaskActions={this.props.locationTaskActions}
                            filesActions={this.props.filesActions}
                            isLoading={isLoadingAnything}
                            locationTasks={this.state.locationTasks}
                            totalCount={this.state.totalCount}
                            filters={this.state.filters}
                            columnFilters={this.state.columnFilters}
                            searchParams={this.props.taskListState.searchParams}
                            allTasksChecked={this.state.allTasksChecked}
                            onSort={this.onColumnSort}
                            updateSearchParams={this.updateSearchParams}
                            onStatusChangedComplete={this.onStatusChangedComplete}
                            onMasterCheckboxToggle={this.onMasterCheckboxToggle}
                            onToggleCheckedTask={this.onToggleCheckedTask}
                            showTask={this.showTask}
                            bulkUpdateMode={this.state.bulkUpdateMode}
                            userId={this.props.userId}
                            onAssignToMeComplete={this.onAssignToMeComplete}
                            bulkUpdateStatus={this.bulkUpdateStatus}
                            bulkUpdateResponsibleUser={this.bulkUpdateResponsibleUser}
                            bulkUpdateByJobTitle={this.bulkUpdateByJobTitle}
                            bulkUpdateInactive={this.bulkUpdateInactive}
                            onGanttExportToPDF={this.onGanttExportToPDF}
                            onColumnsClick={this.onColumnsClick}
                            userRole={this.props.userRole}
                            checkedTasks={this.state.checkedTasks}
                        />
                    );
                }
            }
        }
    };

    onSaveViewClick = () => {
        this.saveFiltersModal.current.show();
    };

    onSaveCustomFilters = async () => {
        if (!this.validateSaveCustomViewForm()) return false;

        this.setState({ isSaveCustomViewModalLoading: true });

        const saveCustomFiltersRequest = {
            name: this.state.customFiltersName.trim(),
            filter: this.props.taskListState.searchParams,
        };

        try {
            const result = await this.props.locationTaskActions.saveCustomFilters(saveCustomFiltersRequest);

            if (result.type === types.SAVE_FILTERS_SUCCESS) {
                Notification('success', 'Save Successful', 'Custom View created');
                this.saveFiltersModal.current.hide();

                var newCustomItems = structuredClone(this.props.taskListState.tasksPageTasksMenuItems);
                newCustomItems.push({ filterName: saveCustomFiltersRequest.name, isCustom: true });
                await this.props.locationTaskActions.setTasksPageTaskMenuItems(newCustomItems);
            } else if (result.type === types.SAVE_FILTERS_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        } finally {
            this.setState({ isSaveCustomViewModalLoading: false });
        }
    };

    validateSaveCustomViewForm = () => {
        if (this.state.customFiltersName.trim().length < 1) {
            this.setState({ isSaveFiltersModalFormValid: false });
            return false;
        } else {
            return true;
        }
    };

    onCustomViewClick = async (filterName) => {
        try {
            // Load the custom filter
            const result = await this.props.locationTaskActions.getCustomFilter(filterName);

            if (result.type === types.GET_CUSTOM_FILTER_SUCCESS) {
                let newParams = {
                    ...this.state.defaultSearchParams,
                    ...result.payload.filters,
                };
                // Dates suck
                newParams.targetDateEnd = newParams.targetDateEnd ? Moment(newParams.targetDateEnd).format(DATE_FORMAT) : null;
                newParams.targetDateStart = newParams.targetDateStart ? Moment(newParams.targetDateStart).format(DATE_FORMAT) : null;
                newParams.startDateEnd = newParams.startDateEnd ? Moment(newParams.startDateEnd).format(DATE_FORMAT) : null;
                newParams.startDateStart = newParams.startDateStart ? Moment(newParams.startDateStart).format(DATE_FORMAT) : null;
                const hasLocationChanged = _.isEqual(newParams.locationKeys, this.props.taskListState.searchParams.locationKeys);

                // Map parameters to URL aliases and construct query string
                const queryString = Object.keys(newParams)
                    .filter((key) => {
                        const aliasExists = URLParams[key.charAt(0).toUpperCase() + key.slice(1)]; // Handle case sensitivity
                        const valueExists = newParams[key] !== null && newParams[key] !== undefined;
                        const isNotEmptyArrayOrString = Array.isArray(newParams[key]) ? newParams[key].length > 0 : newParams[key] !== '';
                        return aliasExists && valueExists && isNotEmptyArrayOrString;
                    })
                    .flatMap((key) => {
                        const alias = URLParams[key.charAt(0).toUpperCase() + key.slice(1)]; // Handle case sensitivity
                        const value = newParams[key];
                        if (Array.isArray(value)) {
                            // For arrays, create multiple key-value pairs
                            return value.map((v) => `${alias}=${encodeURIComponent(v)}`);
                        }
                        // For non-arrays, create a single key-value pair
                        return `${alias}=${encodeURIComponent(value)}`;
                    })
                    .join('&');

                await this.setState({ haveSearchParamsChanged: true }, async () => {
                    await this.props.locationTaskActions.setLocationFilterState(newParams);

                    // Update browser URL
                    this.props.history.push({
                        pathname: this.props.history.location.pathname,
                        search: `?${queryString}`,
                    });

                    await this.search();
                });

                // If the location has changed we need to update other items also
                if (hasLocationChanged) {
                    await this.loadColumnFilters();

                    // Update redux so the drop down is correct
                    this.props.locationTaskActions.setLocationKeys(newParams.locationKeys);
                    await this.getResponsibleUsersForLocations();
                }
            } else if (result.type === types.GET_CUSTOM_FILTER_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        }
    };

    render() {
        const hasNoFilters = _.isEqual(this.state.defaultSearchParams, this.props.taskListState.searchParams);

        return (
            <div className='task-list-page'>
                <DocumentTitle title='Task List' />

                {!this.state.isLoadingSearchParams && (
                    <TaskListHeader
                        isMobile={this.isMobile()}
                        subscriptionId={this.props.subscriptionId}
                        locationTaskActions={this.props.locationTaskActions}
                        taskListState={this.props.taskListState}
                        selectedView={this.state.selectedView}
                        onViewChange={this.onViewChange}
                        onLocationChange={this.onLocationChange}
                        onColumnsClick={this.onColumnsClick}
                        onFiltersClick={this.onShowFiltersDrawer}
                        bulkUpdateMode={this.state.bulkUpdateMode}
                        changeBulkUpdateMode={this.changeBulkUpdateMode}
                        bulkUpdateInactive={this.bulkUpdateInactive}
                        bulkUpdateStatus={this.bulkUpdateStatus}
                        bulkUpdateResponsibleUser={this.bulkUpdateResponsibleUser}
                        bulkUpdateByJobTitle={this.bulkUpdateByJobTitle}
                        hasNoFilters={hasNoFilters}
                        onClearFilters={this.clearAllFilters}
                        userRole={this.props.userRole}
                        showTask={this.showTask}
                        onSaveViewClick={this.onSaveViewClick}
                        onCustomViewClick={this.onCustomViewClick}
                        history={this.props.history}
                        isSaveAndManageTaskFiltersActive={this.state.isSaveAndManageTaskFiltersActive}
                    />
                )}

                <div>
                    {this.state.isMissingActiveLocations ? (
                        <div className='alert-danger'>
                            <p>
                                Your account does not have permission to access any active locations. Please{' '}
                                <a href='#' onClick={() => alert('No way to link to support ticket drawer')}>
                                    submit a Support Ticket
                                </a>{' '}
                                to your subscription administrator(s) to resolve this issue.
                            </p>
                        </div>
                    ) : (
                        this.renderView()
                    )}
                    {this.state.isTaskDetailOpen && (
                        <TaskDetailManager
                            taskId={this.state.editTaskId}
                            locationKey={this.state.editLocationKey}
                            userId={this.props.userId}
                            responsibleUsers={this.state.locationUsers}
                            customerRole={this.props.userRole}
                            onComplete={this.onTaskDetailComplete}
                            onCancel={this.onTaskDetailCancel}
                            filesActions={this.props.filesActions}
                            locationTaskActions={this.props.locationTaskActions}
                            isMobile={this.isMobile()}
                        />
                    )}
                    {this.state.isColumnFiltersDrawerOpen ? (
                        <ColumnFiltersDrawer
                            onComplete={this.onColumnFiltersComplete}
                            onCancel={this.onColumnFiltersCancel}
                            onReset={this.onResetColumnFilters}
                            filters={this.state.filters}
                            locationTasks={this.state.locationTasks}
                            searchParams={this.props.taskListState.searchParams}
                        />
                    ) : null}
                    {this.state.isBulkUpdateResponsibleUserDrawerOpen ? (
                        <BulkUpdateResponsibleUserDrawer
                            locationTaskActions={this.props.locationTaskActions}
                            onComplete={this.bulkUpdateComplete}
                            onCancel={this.bulkUpdateCancel}
                            locationTasks={this.state.locationTasks.filter((m) => m.isChecked)}
                            locationKeys={this.props.taskListState.searchParams.locationKeys}
                            responsibleUsers={this.state.locationUsers}
                            userId={this.props.userId}
                        />
                    ) : null}
                    {this.state.isBulkUpdateStatusDrawerOpen ? (
                        <BulkUpdateStatusDrawer
                            onComplete={this.completeBulkUpdateStatus}
                            onCancel={this.bulkUpdateCancel}
                            statuses={this.state.columnFilters.statuses}
                            locationTasks={this.state.locationTasks.filter((m) => m.isChecked)}
                        />
                    ) : null}
                    {this.state.isShowFiltersDrawerOpen && (
                        <ShowFiltersDrawer
                            onComplete={this.onFiltersDrawerComplete}
                            onCancel={this.onFiltersDrawerCancel}
                            columnFilters={this.state.columnFilters}
                            searchParams={this.props.taskListState.searchParams}
                            updateSearchParams={this.updateSearchParams}
                            isStartDateActive={this.state.filters.isStartDateActive}
                        />
                    )}
                    {this.state.isBulkUpdateByJobTitleOpen && (
                        <AssignTasksDrawer
                            locationDashboardActions={this.props.locationDashboardActions}
                            locationKey={this.props.taskListState.searchParams.locationKeys.toString()}
                            onComplete={this.bulkUpdateByJobTitleComplete}
                            onCancel={this.bulkUpdateByJobTitleCancel}
                        />
                    )}
                    <br />
                    <ReportFooter subscriptionActions={this.props.subscriptionActions} />
                </div>

                <Modal61
                    title='Save View'
                    ref={this.saveFiltersModal}
                    body={
                        <form name='saveViewForm' noValidate className={this.state.isSaveFiltersModalFormValid === true ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <label htmlFor='custom-filters-name' className='title'>
                                    Custom View Name
                                </label>
                                <InputText
                                    name='custom-filters-name'
                                    type='text'
                                    value={this.state.customFiltersName}
                                    onChange={(e) => this.setState({ customFiltersName: e.target.value })}
                                    isRequired={true}
                                    errorMessage='Custom View Name is required'
                                    maxLength={20}
                                    placeholder='What would you like to call this view?'
                                />
                                <small id='custom-view-name-help' className='form-text text-muted sub-title'>
                                    Character Limit 20
                                </small>
                            </div>
                        </form>
                    }
                    primaryButtonText='Save'
                    onPrimaryClick={this.onSaveCustomFilters}
                />
            </div>
        );
    }
}

TaskListPage.propTypes = {
    windowWidth: PropTypes.number,
    subscriptionId: PropTypes.string.isRequired,
    taskListState: PropTypes.object.isRequired,
    filesActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    locationActions: PropTypes.object.isRequired,
    subscriptionActions: PropTypes.object.isRequired,
    locationDashboardActions: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    userRole: PropTypes.string.isRequired,
    userId: PropTypes.number.isRequired,
    tasksPageTasksMenuItems: PropTypes.array.isRequired,
};

function mapStateToProps(state, ownProps) {
    let urlParams = {
        locationKeys: [],
        taskId: '',
        name: '',
        isMilestone: null,
        projectIds: [],
        targetDateStart: null,
        targetDateEnd: null,
        startDateStart: null,
        startDateEnd: null,
        responsibleUserName: '',
        statusIds: [],
        departmentIds: [],
        disciplineIds: [],
        phaseIds: [],
        communityIds: [],
        tagIds: [],
        recentlyUpdated: null, // Not sure what this should be.  Rich just said "Something to filter by recently updated tasks."
        allTasks: null,
        allLocations: false,
    };
    const searchParams = new URLSearchParams(ownProps.location.search);

    // first look to see if All Locations is present, if not, check for any keys passed in
    if (searchParams.has(URLParams.AllLocations)) {
        urlParams.allLocations = searchParams.get(URLParams.AllLocations);
    } else if (searchParams.has(URLParams.LocationKeys)) {
        urlParams.locationKeys = searchParams.getAll(URLParams.LocationKeys);
    }
    if (searchParams.has(URLParams.TaskId)) {
        urlParams.taskId = searchParams.get(URLParams.TaskId);
    }
    if (searchParams.has(URLParams.IsMilestone)) {
        urlParams.isMilestone = searchParams.get(URLParams.IsMilestone);
    }
    if (searchParams.has(URLParams.ProjectId)) {
        urlParams.projectIds = searchParams.getAll(URLParams.ProjectId);
    }
    if (searchParams.has(URLParams.TargetDateStart)) {
        const targetDateStart = searchParams.get(URLParams.TargetDateStart);
        urlParams.targetDateStart = Moment(targetDateStart).format(DATE_FORMAT);
    }
    if (searchParams.has(URLParams.TargetDateEnd)) {
        const targetDateEnd = searchParams.get(URLParams.TargetDateEnd);
        urlParams.targetDateEnd = Moment(targetDateEnd).format(DATE_FORMAT);
    }
    if (searchParams.has(URLParams.StartDateStart)) {
        const startDateStart = searchParams.get(URLParams.StartDateStart);
        urlParams.startDateStart = Moment(startDateStart).format(DATE_FORMAT);
    }
    if (searchParams.has(URLParams.StartDateEnd)) {
        const startDateEnd = searchParams.get(URLParams.StartDateEnd);
        urlParams.startDateEnd = Moment(startDateEnd).format(DATE_FORMAT);
    }
    if (searchParams.has(URLParams.ResponsibleUserName)) {
        urlParams.responsibleUserName = searchParams.get(URLParams.ResponsibleUserName);
    }
    if (searchParams.has(URLParams.StatusId)) {
        urlParams.statusIds = searchParams.getAll(URLParams.StatusId);
    }
    if (searchParams.has(URLParams.DepartmentId)) {
        urlParams.departmentIds = searchParams.getAll(URLParams.DepartmentId);
    }
    if (searchParams.has(URLParams.DisciplineId)) {
        urlParams.disciplineIds = searchParams.getAll(URLParams.DisciplineId);
    }
    if (searchParams.has(URLParams.PhaseId)) {
        urlParams.phaseIds = searchParams.getAll(URLParams.PhaseId);
    }
    if (searchParams.has(URLParams.CommunityId)) {
        urlParams.communityIds = searchParams.getAll(URLParams.CommunityId);
    }
    if (searchParams.has(URLParams.RecentlyUpdated)) {
        urlParams.recentlyUpdated = searchParams.get(URLParams.RecentlyUpdated) === 'false' ? false : true;
    }
    if (searchParams.has(URLParams.AllTasks)) {
        urlParams.allTasks = searchParams.get(URLParams.AllTasks) === 'false' ? false : true;
    }
    if (searchParams.has(URLParams.TagId)) {
        urlParams.tagIds = searchParams.getAll(URLParams.TagId);
    }
    if (searchParams.has(URLParams.Name)) {
        urlParams.name = searchParams.get(URLParams.Name);
    }
    if (searchParams.has(URLParams.BaselineName)) {
        urlParams.baselineName = searchParams.get(URLParams.BaselineName);
    }
    if (searchParams.has(URLParams.AdditionalInfo)) {
        urlParams.additionalInfo = searchParams.get(URLParams.AdditionalInfo);
    }
    if (searchParams.has(URLParams.DropDownOption)) {
        urlParams.dropDownOption = searchParams.get(URLParams.DropDownOption);
    }

    let tasksPageTasksMenuItems = [];
    if (state.taskListState && state.taskListState.tasksPageTasksMenuItems && state.taskListState.tasksPageTasksMenuItems.length) {
        tasksPageTasksMenuItems = state.taskListState.tasksPageTasksMenuItems;
    }

    return {
        windowWidth: state.window.width,
        subscriptionId: state.user.currentSubscriptionId.toString(),
        taskListState: state.taskListState,
        urlParams: urlParams,
        history: ownProps.history,
        userRole: state.user.customerRole,
        userId: state.user.id,
        tasksPageTasksMenuItems: tasksPageTasksMenuItems,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        locationTaskActions: bindActionCreators(locationTaskActions, dispatch),
        subscriptionActions: bindActionCreators(subscriptionActions, dispatch),
        locationDashboardActions: bindActionCreators(locationDashboardActions, dispatch),
        filesActions: bindActionCreators(filesActions, dispatch),
        locationActions: bindActionCreators(locationActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskListPage);
